import {useState} from "react";
import {Button, Col, Form, ListGroup, Row, Table} from "react-bootstrap";
import {StatsApi} from "../../../api/StatsApi";
import {SiteStatMarker, SiteStatMarkerInput, SiteStatsReport} from "../../../api/models";
import {useAsync} from "../../../components/useAsync";
import {ErrorAlert, fieldError} from "../../../components/ErrorAlert";
import {LoadingSpinner} from "../../../components/LoadingSpinner";

const NAME_MAX_LENGTH = 100;
const NOTE_MAX_LENGTH = 1000;
export const COMPARE_WINDOWS = [7, 14, 28] as const;
export type CompareWindow = typeof COMPARE_WINDOWS[number];

const DAY_MS = 24 * 60 * 60 * 1000;
const dayOf = (date: Date) => date.toISOString().substring(0, 10);
const addDays = (day: string, days: number) => dayOf(new Date(new Date(`${day}T00:00:00Z`).valueOf() + days * DAY_MS));
const longDate = new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
export const formatDay = (day: string) => longDate.format(new Date(`${day}T00:00:00Z`));
const percent = new Intl.NumberFormat(undefined, { style: 'percent', maximumFractionDigits: 0, signDisplay: 'exceptZero' });

const blank = (): SiteStatMarkerInput => ({ name: '', note: '', day: dayOf(new Date()) });

// The days compared for a marker on day, with window days on each side: from its day to window days on (no further
// than today), and as many days just before it. Undefined while its day is still to come.
export function compareRanges(day: string, window: number, today: string = dayOf(new Date())):
    { before: { from: string, to: string }, after: { from: string, to: string }, days: number, partial: boolean } | undefined {
    if (day > today) {
        return undefined;
    }
    const lastWanted = addDays(day, window - 1);
    const to = lastWanted < today ? lastWanted : today;
    const days = Math.round((new Date(`${to}T00:00:00Z`).valueOf() - new Date(`${day}T00:00:00Z`).valueOf()) / DAY_MS) + 1;
    return {
        before: { from: addDays(day, -days), to: addDays(day, -1) },
        after: { from: day, to },
        days,
        partial: to === today
    };
}

// Named days in the visit counts: adding, changing and deleting them, and picking one to compare. markers is the
// page's list (latest day first), onChange gives it the new one.
export function Markers({markers, onChange, selected, onSelect}: {
    markers: SiteStatMarker[], onChange: (markers: SiteStatMarker[]) => void,
    selected?: string, onSelect: (id: string | undefined) => void
}) {
    const [form, setForm] = useState<SiteStatMarkerInput>(blank);
    const [editing, setEditing] = useState<string>();
    const [deleting, setDeleting] = useState<string>();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();
    const sorted = (list: SiteStatMarker[]) => [...list].sort((a, b) => b.day.localeCompare(a.day) || b.createdOn.localeCompare(a.createdOn));

    const run = async (action: () => Promise<void>) => {
        setBusy(true);
        setError(undefined);
        try {
            await action();
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };

    const save = () => run(async () => {
        const input = { name: form.name.trim(), note: form.note.trim(), day: form.day };
        if (editing) {
            const changed = await StatsApi.changeMarker(editing, input);
            onChange(sorted(markers.map(x => x.id === editing ? changed : x)));
        } else {
            const added = await StatsApi.addMarker(input);
            onChange(sorted([added, ...markers]));
        }
        setEditing(undefined);
        setForm(blank());
    });

    const remove = (id: string) => run(async () => {
        await StatsApi.deleteMarker(id);
        setDeleting(undefined);
        if (selected === id) {
            onSelect(undefined);
        }
        onChange(markers.filter(x => x.id !== id));
    });

    const edit = (marker: SiteStatMarker) => {
        setEditing(marker.id);
        setDeleting(undefined);
        setForm({ name: marker.name, note: marker.note, day: marker.day });
    };

    return (
        <>
            <ErrorAlert error={error} shownFields={['name', 'note', 'day']} messages={{ 404: 'That marker was already deleted.' }} onClose={() => setError(undefined)}/>
            <Form onSubmit={e => { e.preventDefault(); void save(); }} className="mb-3" aria-label={editing ? 'Change marker' : 'Add a marker'}>
                <Row className="g-2 align-items-start">
                    <Col md={4}>
                        <Form.Group controlId="marker-name">
                            <Form.Label className="small mb-1">Name</Form.Label>
                            <Form.Control size="sm" maxLength={NAME_MAX_LENGTH} placeholder="e.g. Reddit campaign" value={form.name}
                                          isInvalid={!!fieldError(error, 'name')} onChange={e => setForm({ ...form, name: e.target.value })}/>
                            <Form.Control.Feedback type="invalid">{fieldError(error, 'name')}</Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group controlId="marker-day">
                            <Form.Label className="small mb-1">First day (UTC)</Form.Label>
                            <Form.Control size="sm" type="date" value={form.day} isInvalid={!!fieldError(error, 'day')}
                                          onChange={e => setForm({ ...form, day: e.target.value })}/>
                            <Form.Control.Feedback type="invalid">{fieldError(error, 'day')}</Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col md={5}>
                        <Form.Group controlId="marker-note">
                            <Form.Label className="small mb-1">Note (optional)</Form.Label>
                            <Form.Control size="sm" maxLength={NOTE_MAX_LENGTH} value={form.note} isInvalid={!!fieldError(error, 'note')}
                                          onChange={e => setForm({ ...form, note: e.target.value })}/>
                            <Form.Control.Feedback type="invalid">{fieldError(error, 'note')}</Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>
                <div className="mt-2">
                    <Button size="sm" type="submit" variant="outline-primary" className="me-2" disabled={busy || !form.name.trim() || !form.day}>
                        {editing ? 'Save marker' : 'Add marker'}
                    </Button>
                    {editing && <Button size="sm" variant="outline-secondary" disabled={busy} onClick={() => { setEditing(undefined); setForm(blank()); }}>Cancel</Button>}
                </div>
            </Form>
            {markers.length === 0 ? <p className="text-body-secondary small">No markers yet.</p> : (
                <ListGroup as="ul" variant="flush" aria-label="Markers" className="mb-3">
                    {markers.map(marker => (
                        <ListGroup.Item as="li" key={marker.id} className="px-0">
                            <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
                                <div>
                                    <span className="fw-semibold">{marker.name}</span>{' '}
                                    <span className="small text-body-secondary">{formatDay(marker.day)}</span>
                                    {marker.note && <div className="small">{marker.note}</div>}
                                </div>
                                <span className="text-nowrap">
                                    {deleting === marker.id ? (
                                        <>
                                            <Button size="sm" variant="danger" className="me-1" disabled={busy} onClick={() => remove(marker.id)}>Delete</Button>
                                            <Button size="sm" variant="outline-secondary" disabled={busy} onClick={() => setDeleting(undefined)}>Keep</Button>
                                        </>
                                    ) : (
                                        <>
                                            <Button size="sm" variant={selected === marker.id ? 'primary' : 'outline-primary'} className="me-1"
                                                    aria-pressed={selected === marker.id}
                                                    onClick={() => onSelect(selected === marker.id ? undefined : marker.id)}>Compare</Button>
                                            <Button size="sm" variant="link" className="p-0 me-2" disabled={busy} onClick={() => edit(marker)}>Edit</Button>
                                            <Button size="sm" variant="link" className="p-0 text-danger" disabled={busy}
                                                    onClick={() => setDeleting(marker.id)}>Delete…</Button>
                                        </>
                                    )}
                                </span>
                            </div>
                        </ListGroup.Item>
                    ))}
                </ListGroup>
            )}
        </>
    );
}

interface Totals {
    visits: number;
    pageViews: number;
    productViews: number;
    checkoutViews: number;
    releaseSignups: number;
    guestDownloads: number;
    downloads: number;
    checkoutsStarted: number;
    purchased: number;
}

function totals(report: SiteStatsReport): Totals {
    const daily = (field: 'visits' | 'pageViews' | 'productViews' | 'checkoutViews') => report.daily.reduce((sum, x) => sum + x[field], 0);
    const products = (field: 'releaseSignups' | 'guestDownloads' | 'downloads' | 'checkoutsStarted' | 'purchased') => report.products.reduce((sum, x) => sum + (x[field] ?? 0), 0);
    return {
        visits: daily('visits'),
        pageViews: daily('pageViews'),
        productViews: daily('productViews'),
        checkoutViews: daily('checkoutViews'),
        releaseSignups: products('releaseSignups'),
        guestDownloads: products('guestDownloads'),
        downloads: products('downloads'),
        checkoutsStarted: products('checkoutsStarted'),
        purchased: products('purchased')
    };
}

const ROWS: { field: keyof Totals, label: string }[] = [
    { field: 'visits', label: 'Visits' },
    { field: 'pageViews', label: 'Page views' },
    { field: 'productViews', label: 'Product views' },
    { field: 'checkoutViews', label: 'Checkout views' },
    { field: 'releaseSignups', label: 'Release sign-ups' },
    { field: 'guestDownloads', label: 'Trial downloads' },
    { field: 'downloads', label: 'Signed-in downloads' },
    { field: 'checkoutsStarted', label: 'Checkouts started' },
    { field: 'purchased', label: 'Purchases' }
];

function Change({before, after}: { before: number, after: number }) {
    if (before === after) {
        return <span className="text-body-secondary">–</span>;
    }
    const change = after - before;
    return <>{change > 0 ? `+${change}` : change} <span className="small text-body-secondary">{before > 0 ? percent.format(change / before) : 'new'}</span></>;
}

// The marker's days against as many days just before it: the totals, each product's, and the sources that are new.
export function MarkerComparison({marker, window, onWindow}: { marker: SiteStatMarker, window: CompareWindow, onWindow: (window: CompareWindow) => void }) {
    const ranges = compareRanges(marker.day, window);
    const {data, error, loading} = useAsync(async () => ranges
        ? Promise.all([StatsApi.report(ranges.before.from, ranges.before.to), StatsApi.report(ranges.after.from, ranges.after.to)])
        : undefined, [marker.day, window]);

    const span = (range: { from: string, to: string }) => range.from === range.to ? formatDay(range.from) : `${formatDay(range.from)} – ${formatDay(range.to)}`;
    return (
        <section aria-label={`Before and after ${marker.name}`} className="mb-4">
            <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                <h3 className="h6 mb-0">{marker.name}: before and after</h3>
                <Form.Select size="sm" className="w-auto" aria-label="Days each side" value={window}
                             onChange={e => onWindow(Number(e.target.value) as CompareWindow)}>
                    {COMPARE_WINDOWS.map(x => <option key={x} value={x}>{x} days each side</option>)}
                </Form.Select>
            </div>
            {!ranges && <p className="text-body-secondary small">Its day hasn't come yet: there's nothing to compare.</p>}
            {ranges && (
                <p className="text-body-secondary small">
                    {ranges.days} {ranges.days === 1 ? 'day' : 'days'} each side: before {span(ranges.before)}, from it {span(ranges.after)}.
                    {ranges.days < window && ' Fewer than chosen, since it was recent.'}
                    {ranges.partial && ' Today is still in progress, so its side is lower than it will be.'}
                    {ranges.days < 7 && ' A short stretch: a few visits either way make a large difference.'}
                </p>
            )}
            {loading && !data && <LoadingSpinner/>}
            <ErrorAlert error={error}/>
            {data && <ComparisonTables before={data[0]} after={data[1]}/>}
        </section>
    );
}

function ComparisonTables({before, after}: { before: SiteStatsReport, after: SiteStatsReport }) {
    const b = totals(before);
    const a = totals(after);
    const productNames = [...new Set([...after.products, ...before.products].map(x => x.name))];
    const productOf = (report: SiteStatsReport, name: string) => report.products.find(x => x.name === name);
    const known = new Set(before.sources.map(x => x.name));
    const newSources = after.sources.filter(x => !known.has(x.name));
    return (
        <Row className="g-4">
            <Col lg={6}>
                <Table size="sm" responsive>
                    <thead><tr><th></th><th className="text-end">Before</th><th className="text-end">From it</th><th className="text-end">Change</th></tr></thead>
                    <tbody>
                    {ROWS.map(row => (
                        <tr key={row.field}>
                            <td>{row.label}</td>
                            <td className="text-end">{b[row.field]}</td>
                            <td className="text-end">{a[row.field]}</td>
                            <td className="text-end"><Change before={b[row.field]} after={a[row.field]}/></td>
                        </tr>
                    ))}
                    </tbody>
                </Table>
                {productNames.length > 0 && (
                    <Table size="sm" responsive>
                        <thead><tr><th>Product</th><th className="text-end">Views before</th><th className="text-end">From it</th><th className="text-end">Purchases before</th><th className="text-end">From it</th></tr></thead>
                        <tbody>
                        {productNames.map(name => (
                            <tr key={name}>
                                <td>{name}</td>
                                <td className="text-end">{productOf(before, name)?.productViews ?? 0}</td>
                                <td className="text-end">{productOf(after, name)?.productViews ?? 0}</td>
                                <td className="text-end">{productOf(before, name)?.purchased ?? 0}</td>
                                <td className="text-end">{productOf(after, name)?.purchased ?? 0}</td>
                            </tr>
                        ))}
                        </tbody>
                    </Table>
                )}
            </Col>
            <Col lg={6}>
                <h4 className="h6">Sources new since it</h4>
                {newSources.length === 0 ? <p className="text-body-secondary small">None: every source from it was there before it too.</p> : (
                    <Table size="sm" responsive>
                        <thead><tr><th>Source</th><th className="text-end">Visits</th></tr></thead>
                        <tbody>{newSources.map(x => <tr key={x.name}><td className="text-break">{x.name}</td><td className="text-end">{x.count}</td></tr>)}</tbody>
                    </Table>
                )}
            </Col>
        </Row>
    );
}
